import React, { useState, useEffect } from 'react';

/**
 * Renders a tagline with a word-by-word slide-in animation.
 * When `repeat` is true the animation replays every 5 seconds.
 * Uses a key-flip to re-mount the spans and retrigger the CSS animation.
 */
export default function AnimatedTagline({ tagline, className = '', wordClassName = '', style = {} }) {
  const [cycle, setCycle] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Total animation duration: last word delay + animation duration
    const words = tagline ? tagline.split(' ') : [];
    const totalAnimMs = (words.length - 1) * 120 + 450; // last word finishes here

    // After animation completes, wait until 5s total then fade out and replay
    const pauseMs = 5000 - totalAnimMs;

    let fadeTimer, resetTimer;

    const schedule = () => {
      // Wait for the pause after animation finishes
      fadeTimer = setTimeout(() => {
        setVisible(false); // fade out
        resetTimer = setTimeout(() => {
          setCycle(c => c + 1); // re-mount words → retrigger animation
          setVisible(true);
        }, 300); // 300ms fade-out duration
      }, Math.max(pauseMs, 800));
    };

    schedule();
    return () => { clearTimeout(fadeTimer); clearTimeout(resetTimer); };
  }, [cycle, tagline]);

  if (!tagline) return null;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex', flexWrap: 'wrap', gap: '0.3em', alignItems: 'center',
        transition: 'opacity 0.3s ease',
        opacity: visible ? 1 : 0,
        ...style,
      }}
    >
      {tagline.split(' ').map((word, i) => (
        <span
          key={`${cycle}-${i}`}
          className={wordClassName}
          style={{ animationDelay: `${i * 0.12}s` }}
        >
          {word}
        </span>
      ))}
    </span>
  );
}
